import { Badge, Button, Collapse, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import React, { useEffect, useState } from "react";
import { ProductConfig } from "types/productManagementTypes";
import ConfigJsonView from "./ConfigJsonView";
import { DefaultConfigState, applicationStatus, versionInfoLine } from "./defaultConfigMatch";

interface DeviceDefaultConfigPanelProps {
  deviceConfig: ProductConfig;
  state: DefaultConfigState;
}

const DeviceDefaultConfigPanel: React.FC<DeviceDefaultConfigPanelProps> = ({
  deviceConfig,
  state,
}) => {
  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    setShowConfig(false);
  }, [state]);

  return (
    <Paper withBorder radius="sm" p="md">
      <Stack gap="xs">
        <Stack gap={2}>
          <Text fw={600} size="sm">
            Default config for this device
          </Text>
          <Text size="xs" c="dimmed">
            A default config applies to every device matching its hardware model, PUI version and
            app version. A device can instead have a config set directly for its PID, which then
            takes over.
          </Text>
        </Stack>
        {state.status === "loading" && (
          <Group gap="xs">
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Resolving default config…
            </Text>
          </Group>
        )}
        {state.status === "error" && (
          <Text size="sm" c="red">
            {state.msg}
          </Text>
        )}
        {state.status === "noVersionInfo" && (
          <Text size="sm" c="dimmed">
            The device&apos;s PUI version or app version is unknown, so no default config can be
            matched.
          </Text>
        )}
        {state.status === "noMatch" && (
          <Text size="sm" c="dimmed">
            No default config matches this device ({versionInfoLine(state.versionInfo)}).
          </Text>
        )}
        {state.status === "match" && (
          <Stack gap="xs">
            <Group gap="xs">
              <Badge variant="light" color="blue">
                Match
              </Badge>
              <Badge variant="light" color={applicationStatus(deviceConfig).color}>
                {applicationStatus(deviceConfig).label}
              </Badge>
              <Text size="sm">
                {state.defaultConfig.defaultConfigName
                  ? `${state.defaultConfig.defaultConfigName} (${versionInfoLine(state.versionInfo)})`
                  : versionInfoLine(state.versionInfo)}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {applicationStatus(deviceConfig).detail}
            </Text>
            <div>
              <Button size="compact-xs" variant="default" onClick={() => setShowConfig((s) => !s)}>
                {showConfig ? "Hide config" : "Show config"}
              </Button>
            </div>
            <Collapse in={showConfig}>
              <ConfigJsonView value={state.defaultConfig.config} />
            </Collapse>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default DeviceDefaultConfigPanel;
